import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import tw, { TwStyle } from 'twin.macro'
import { Typography } from '@mui/material'
import { AiReviewConfigActionsCell } from 'src/components/ai-review/grid/AiReviewConfigActionsCell'
import { Chip } from 'src/components/shared/Chip'
import { AI_REVIEW_ALLOWED_ACTIONS_LABELS } from 'src/constants/ai-review/constants'
import { stepLabel } from 'src/constants/collect-data/stepTypeLabels'
import type { AiDocumentReviewConfig } from 'src/interfaces/aiDocumentReviewConfig'
import type { AiReviewModeLabel } from 'src/utils/aiReviewConfigFormatters.types'
import {
  formatChecks,
  formatInstructions,
  formatStepLabel,
  formatThreshold,
  hasMissingThresholdWarning,
  resolveAllowedActions,
  resolveModeLabel,
} from 'src/utils/aiReviewConfigFormatters'
import { aiReviewConfigsTableHeaderTitles } from './constants'
import { AiReviewConfigsTableFields } from './enums'

const MODE_CHIP_STYLES: Record<AiReviewModeLabel, TwStyle> = {
  Active: tw`bg-primary-100`,
  Shadow: tw`bg-shades-100`,
  'Review off': tw`bg-shades-100`,
}

const MODE_TEXT_STYLES: Record<AiReviewModeLabel, TwStyle> = {
  Active: tw`text-primary-700`,
  Shadow: tw`text-shades-600`,
  'Review off': tw`text-shades-500`,
}

/**
 * A factory rather than a module constant: the actions column closes over the page's edit and
 * delete handlers. Callers must memoize the result, since a fresh array on every render would
 * reset the grid's column state.
 *
 * Sorting is client-side and allowed here: the endpoint returns the partner's whole list in one
 * call, so there is no server-pagination mismatch of the kind that forced `sortable: false` on
 * the Users table.
 */
export const buildAiReviewConfigColumns = (
  onEdit: (config: AiDocumentReviewConfig) => void,
  onDelete: (config: AiDocumentReviewConfig) => void,
  // `ai_document_review_configs_manage` (ENG-1207 part 2). Edit and Delete both need it, so
  // without it the actions column is dropped rather than left as an empty menu.
  canManage: boolean,
): Array<GridColDef<AiDocumentReviewConfig>> => {
  const columns: Array<GridColDef<AiDocumentReviewConfig>> = [
    {
      field: AiReviewConfigsTableFields.stepType,
      headerName: aiReviewConfigsTableHeaderTitles[AiReviewConfigsTableFields.stepType],
      flex: 2,
      minWidth: 180,
      sortable: true,
      // The document identifies the row, so it is never offered for hiding.
      hideable: false,
      display: 'flex',
      valueGetter: (value, row: AiDocumentReviewConfig) => {
        return stepLabel(row.stepType)
      },
      renderCell: (params: GridRenderCellParams<AiDocumentReviewConfig>) => {
        return (
          <Typography variant="body2" css={tw`truncate`}>
            {stepLabel(params.row.stepType)}
          </Typography>
        )
      },
    },
    {
      field: AiReviewConfigsTableFields.mode,
      headerName: aiReviewConfigsTableHeaderTitles[AiReviewConfigsTableFields.mode],
      flex: 1,
      minWidth: 130,
      sortable: true,
      display: 'flex',
      valueGetter: (value, row: AiDocumentReviewConfig) => {
        return resolveModeLabel(row)
      },
      renderCell: (params: GridRenderCellParams<AiDocumentReviewConfig>) => {
        const modeLabel = resolveModeLabel(params.row)

        return (
          <Chip
            text={modeLabel}
            className={MODE_CHIP_STYLES[modeLabel]}
            textClassName={MODE_TEXT_STYLES[modeLabel]}
          />
        )
      },
    },
    {
      field: AiReviewConfigsTableFields.label,
      headerName: aiReviewConfigsTableHeaderTitles[AiReviewConfigsTableFields.label],
      flex: 2,
      minWidth: 170,
      sortable: true,
      display: 'flex',
      valueGetter: (value, row: AiDocumentReviewConfig) => {
        return formatStepLabel(row)
      },
      renderCell: (params: GridRenderCellParams<AiDocumentReviewConfig>) => {
        return (
          <Typography variant="body2" css={tw`truncate`}>
            {formatStepLabel(params.row)}
          </Typography>
        )
      },
    },
    {
      field: AiReviewConfigsTableFields.allowedActions,
      headerName: aiReviewConfigsTableHeaderTitles[AiReviewConfigsTableFields.allowedActions],
      flex: 1,
      minWidth: 150,
      sortable: true,
      display: 'flex',
      valueGetter: (value, row: AiDocumentReviewConfig) => {
        return AI_REVIEW_ALLOWED_ACTIONS_LABELS[resolveAllowedActions(row)]
      },
      renderCell: (params: GridRenderCellParams<AiDocumentReviewConfig>) => {
        return (
          <Typography variant="body2" css={tw`truncate`}>
            {AI_REVIEW_ALLOWED_ACTIONS_LABELS[resolveAllowedActions(params.row)]}
          </Typography>
        )
      },
    },
    {
      field: AiReviewConfigsTableFields.autoApproveMinSlpRate,
      headerName:
        aiReviewConfigsTableHeaderTitles[AiReviewConfigsTableFields.autoApproveMinSlpRate],
      flex: 1,
      minWidth: 180,
      sortable: true,
      display: 'flex',
      valueGetter: (value, row: AiDocumentReviewConfig) => {
        return formatThreshold(row)
      },
      renderCell: (params: GridRenderCellParams<AiDocumentReviewConfig>) => {
        return (
          <div css={tw`flex min-w-0 flex-row items-center gap-2`}>
            <Typography variant="body2" css={tw`truncate`}>
              {formatThreshold(params.row)}
            </Typography>
            {hasMissingThresholdWarning(params.row) ? (
              <Chip
                text="No threshold"
                className={tw`shrink-0 bg-warning-100`}
                textClassName={tw`text-warning-700`}
              />
            ) : null}
          </div>
        )
      },
    },
    {
      field: AiReviewConfigsTableFields.checks,
      headerName: aiReviewConfigsTableHeaderTitles[AiReviewConfigsTableFields.checks],
      flex: 2,
      minWidth: 180,
      sortable: true,
      display: 'flex',
      valueGetter: (value, row: AiDocumentReviewConfig) => {
        return formatChecks(row)
      },
      renderCell: (params: GridRenderCellParams<AiDocumentReviewConfig>) => {
        return (
          <Typography variant="body2" css={tw`truncate`}>
            {formatChecks(params.row)}
          </Typography>
        )
      },
    },
    {
      field: AiReviewConfigsTableFields.instructions,
      headerName: aiReviewConfigsTableHeaderTitles[AiReviewConfigsTableFields.instructions],
      flex: 1,
      minWidth: 130,
      sortable: true,
      display: 'flex',
      valueGetter: (value, row: AiDocumentReviewConfig) => {
        return formatInstructions(row)
      },
      renderCell: (params: GridRenderCellParams<AiDocumentReviewConfig>) => {
        const instructions = formatInstructions(params.row)

        return (
          <Typography
            variant="body2"
            css={[tw`truncate`, instructions === '—' && tw`text-shades-500`]}
          >
            {instructions}
          </Typography>
        )
      },
    },
    {
      field: AiReviewConfigsTableFields.actions,
      headerName: aiReviewConfigsTableHeaderTitles[AiReviewConfigsTableFields.actions],
      width: 68,
      sortable: false,
      hideable: false,
      disableExport: true,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<AiDocumentReviewConfig>) => {
        return <AiReviewConfigActionsCell config={params.row} onEdit={onEdit} onDelete={onDelete} />
      },
    },
  ]

  if (canManage) {
    return columns
  }

  return columns.filter((column) => {
    return column.field !== AiReviewConfigsTableFields.actions
  })
}
